import { LayoutModule, StompService, Tenant } from "@allbesmart/web-app-common";
import {
  UserRole,
  WebAppKeycloakAuthModule,
} from "@allbesmart/web-app-keycloak-auth";
import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
  UrlTree,
} from "@angular/router";
import Keycloak from "keycloak-js";
import { Subscription } from "rxjs";
import { environment } from "../../environments/environment";
import { AvailableRoutes } from "../enums/available-routes";
import { TenantManagementService } from "../services/tenant-management.service";

@Component({
  selector: "oaibox-backoffice-root",
  templateUrl: "./backoffice-root.component.html",
  styleUrls: ["./backoffice-root.component.css"],
  imports: [LayoutModule, RouterLink, WebAppKeycloakAuthModule, RouterOutlet],
})
export class BackofficeRootComponent implements OnInit, OnDestroy {
  protected readonly isUnprivilegedSales: boolean;
  protected readonly UserRole = UserRole;
  protected readonly AvailableRoutes = AvailableRoutes;

  protected machinesUrl?: UrlTree;

  public deploymentsListUrl?: UrlTree;
  public gnbDefaultConfigurationUrl?: UrlTree;
  public stockManagementUrl?: UrlTree;
  public usersUrl?: UrlTree;
  public newUserUrl?: UrlTree;
  public releasesUrl?: UrlTree;
  public newReleaseUrl?: UrlTree;
  public licensesUrl?: UrlTree;
  public operationsUrl?: UrlTree;
  public ordersUrl?: UrlTree;

  public currentRoute?: AvailableRoutes;
  private routeChangesSubscription?: Subscription;

  public client?: Tenant;
  private tenantSubscription?: Subscription;

  constructor(
    private router: Router,
    private tenantManagementService: TenantManagementService,

    stompService: StompService,
    keycloak: Keycloak
  ) {
    const token = keycloak.token;
    if (token)
      stompService.connect(
        token,
        environment.ws.brokerUrl,
        environment.ws.heartbeatIncoming,
        () => keycloak.updateToken(30).then(() => keycloak.token!)
      );

    this.isUnprivilegedSales =
      keycloak.hasRealmRole(UserRole.SALES) &&
      !(
        keycloak.hasRealmRole(UserRole.ADMIN) ||
        keycloak.hasRealmRole(UserRole.ROOT)
      );

    this.machinesUrl = router.createUrlTree([
      AvailableRoutes.STOCK,
      AvailableRoutes.STOCK_MACHINES,
    ]);
    this.deploymentsListUrl = router.createUrlTree([
      AvailableRoutes.ADMIN,
      AvailableRoutes.DEPLOYMENTS,
    ]);
    this.gnbDefaultConfigurationUrl = router.createUrlTree([
      AvailableRoutes.ADMIN,
      AvailableRoutes.GNB_DEFAULT_CONFIGURATION,
    ]);
    this.stockManagementUrl = router.createUrlTree([AvailableRoutes.STOCK]);
    this.newUserUrl = router.createUrlTree([AvailableRoutes.NEW_USER]);
    this.usersUrl = router.createUrlTree([AvailableRoutes.USERS]);
    this.releasesUrl = router.createUrlTree([AvailableRoutes.RELEASES]);
    this.newReleaseUrl = router.createUrlTree([AvailableRoutes.NEW_RELEASE]);
    this.licensesUrl = router.createUrlTree([AvailableRoutes.LICENSES]);
    this.operationsUrl = router.createUrlTree([AvailableRoutes.OPERATIONS]);
    this.ordersUrl = router.createUrlTree([AvailableRoutes.ORDERS]);
  }

  ngOnInit(): void {
    this.handleRouteChange(window.location.pathname);
    this.routeChangesSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) this.handleRouteChange(event.url);
    });
  }

  private handleRouteChange(url: string): void {
    this.currentRoute = this.getCurrentRoute(url);
    this.client = undefined;

    if (
      this.currentRoute === AvailableRoutes.GNB_CUSTOM_CONFIGURATION ||
      this.currentRoute === AvailableRoutes.UPDATE_DEPLOYMENT
    ) {
      const tenantId = url.split("/")[3];

      this.tenantSubscription?.unsubscribe();
      this.tenantSubscription = this.tenantManagementService
        .fetchTenant(tenantId)
        .subscribe((tenant) => (this.client = tenant));
    }
  }

  private getCurrentRoute(url: string): AvailableRoutes {
    if (url.includes(`/${AvailableRoutes.GNB_CUSTOM_CONFIGURATION}`))
      return AvailableRoutes.GNB_CUSTOM_CONFIGURATION;
    if (url.includes(`/${AvailableRoutes.NEW_DEPLOYMENT}`))
      return AvailableRoutes.NEW_DEPLOYMENT;
    if (url.includes(`/${AvailableRoutes.UPDATE_DEPLOYMENT}`))
      return AvailableRoutes.UPDATE_DEPLOYMENT;
    if (url.includes(`/${AvailableRoutes.DEPLOYMENTS}`))
      return AvailableRoutes.DEPLOYMENTS;
    if (url.includes(`/${AvailableRoutes.GNB_DEFAULT_CONFIGURATION}`))
      return AvailableRoutes.GNB_DEFAULT_CONFIGURATION;
    if (url.includes(`/${AvailableRoutes.STOCK_MACHINES}`))
      return AvailableRoutes.STOCK_MACHINES;
    if (url.includes(`/${AvailableRoutes.NEW_USER}`))
      return AvailableRoutes.NEW_USER;
    if (url.includes(`/${AvailableRoutes.USERS}`)) return AvailableRoutes.USERS;
    if (url.includes(`/${AvailableRoutes.RELEASES}`))
      return AvailableRoutes.RELEASES;
    if (url.includes(`/${AvailableRoutes.NEW_RELEASE}`))
      return AvailableRoutes.NEW_RELEASE;
    if (url.includes(`/${AvailableRoutes.LICENSES}`))
      return AvailableRoutes.LICENSES;
    if (url.includes(`/${AvailableRoutes.OPERATIONS}`))
      return AvailableRoutes.OPERATIONS;
    if (url.includes(`/${AvailableRoutes.ORDERS}`))
      return AvailableRoutes.ORDERS;
    return AvailableRoutes.DEPLOYMENTS;
  }

  ngOnDestroy(): void {
    this.routeChangesSubscription?.unsubscribe();
    this.tenantSubscription?.unsubscribe();
  }
}
